import createClient, { type Middleware } from "openapi-fetch";
import { refreshAccessToken } from "../auth/refresh";
import { getAccessToken, getRefreshToken } from "../auth/tokens";
import type { paths } from "./generated/schema";

const authMiddleware: Middleware = {
  async onRequest({ request }) {
    // Respect an Authorization header the caller set explicitly (e.g. the
    // device token for shift-roster/login) — only fall back to the operator
    // access token when no header was provided.
    const token = getAccessToken();
    if (token && !request.headers.has("Authorization")) {
      request.headers.set("Authorization", `Bearer ${token}`);
    }
    return request;
  },
};

/**
 * Custom fetch that transparently refreshes the access token on a 401 and
 * retries the original request once. Clones the request *before* the first
 * send so a retried POST still has its body. Avoids logging operators out
 * mid-shift when the 15-minute access token expires.
 */
const authFetch: typeof fetch = async (input, init) => {
  const original = input instanceof Request ? input : new Request(input, init);
  const retryClone = original.clone();
  const resp = await fetch(original);
  if (resp.status !== 401 || !getRefreshToken()) return resp;

  const refreshed = await refreshAccessToken();
  if (!refreshed) return resp;

  const token = getAccessToken();
  if (token) retryClone.headers.set("Authorization", `Bearer ${token}`);
  return fetch(retryClone);
};

// `baseUrl` is empty: the OpenAPI schema paths already include the `/api/v1`
// prefix (drf-spectacular generates them that way). Call sites pass the
// full path; types stay in sync with the server router.
export const api = createClient<paths>({ baseUrl: "", fetch: authFetch });
api.use(authMiddleware);
